<!-- eslint-disable vue/no-bare-strings-in-template -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const {
  content,
  contentAttributes,
  attachments,
  id: messageId,
  conversationId,
  orientation,
  sender,
} = useMessageContext();
const accountId = useMapGetter('getCurrentAccountId');
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasLumerAIFeature = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.LUMERAI_TASKS
  );
});

const igAttributes = computed(() => {
  return contentAttributes.value || {};
});

const replies = computed(() => {
  if (!currentChat.value?.messages) return [];
  const rootCommentId =
    igAttributes.value.fb_comment_id || igAttributes.value.fbCommentId;
  if (!rootCommentId) return [];

  const publicReplies = currentChat.value.messages.filter(m => {
    const isPublic =
      m.content_attributes?.fb_reply_type === 'public' ||
      m.content_attributes?.fbReplyType === 'public';
    return isPublic;
  });

  const childrenByParent = publicReplies.reduce((groups, reply) => {
    const parentId =
      reply.content_attributes?.in_reply_to_external_id ||
      reply.content_attributes?.inReplyToExternalId;
    if (!groups[parentId]) groups[parentId] = [];
    groups[parentId].push(reply);
    return groups;
  }, {});

  const threadedReplies = [];
  const appendReplies = (parentId, depth) => {
    (childrenByParent[parentId] || []).forEach(reply => {
      threadedReplies.push({ ...reply, threadDepth: depth });
      appendReplies(reply.source_id, depth + 1);
    });
  };

  appendReplies(rootCommentId, 0);
  return threadedReplies;
});

const replyThreadClass = depth => {
  const classes = ['ml-0', 'ml-4', 'ml-8', 'ml-12', 'ml-16'];
  return classes[Math.min(depth || 0, classes.length - 1)];
};

const imageLoadFailed = ref(false);
const storedImageUrl = computed(
  () =>
    attachments.value?.find(attachment => attachment.file_type === 'image')
      ?.data_url
);
const imageUrl = computed(
  () =>
    storedImageUrl.value ||
    igAttributes.value.image_url ||
    igAttributes.value.imageUrl
);
const caption = computed(() => igAttributes.value.caption);
const commentAuthorName = computed(
  () =>
    sender.value?.name ||
    igAttributes.value.author_name ||
    igAttributes.value.fb_username ||
    'Pessoa no Facebook'
);
const commentAuthorAvatar = computed(
  () =>
    sender.value?.thumbnail ||
    igAttributes.value.author_avatar_url ||
    igAttributes.value.profile_picture_url ||
    null
);
const commentAuthorInitials = computed(() =>
  commentAuthorName.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('')
);
const permalink = computed(() => igAttributes.value.permalink);
const embedUrl = computed(() => {
  if (!permalink.value) return null;
  // Ensure the URL ends with /embed or /embed/
  return permalink.value.replace(/\/?$/, '/embed');
});

const suggestions = ref([]);
const isGenerating = ref(false);
const replyText = ref('');
const localIsHidden = ref(null);
const localIsDeleted = ref(null);
const localIsLiked = ref(null);
const isChangingLike = ref(false);

// Reply specific state
const generatingReplies = ref({});
const replySuggestions = ref({});
const localHiddenReplies = ref({});

const isHidden = computed(() =>
  localIsHidden.value !== null
    ? localIsHidden.value
    : igAttributes.value.is_hidden ||
      igAttributes.value.isHidden ||
      igAttributes.value.facebook_hidden ||
      false
);
const isDeleted = computed(() =>
  localIsDeleted.value !== null
    ? localIsDeleted.value
    : igAttributes.value.deleted || false
);
const isLiked = computed(() =>
  localIsLiked.value !== null
    ? localIsLiked.value
    : igAttributes.value.is_liked_by_business ||
      igAttributes.value.isLikedByBusiness ||
      igAttributes.value.liked_by_page ||
      igAttributes.value.likedByPage ||
      false
);

const openPost = () => {
  if (permalink.value) {
    window.open(permalink.value, '_blank', 'noopener,noreferrer');
  }
};

const generateSuggestions = async e => {
  e.stopPropagation();
  if (isGenerating.value) return;

  if (
    igAttributes.value.ai_suggestions &&
    igAttributes.value.ai_suggestions.length > 0
  ) {
    suggestions.value = igAttributes.value.ai_suggestions;
    return;
  }

  isGenerating.value = true;
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${messageId.value}/suggest_replies`
    );
    suggestions.value = response.data.suggestions || [];
    if (suggestions.value.length === 0) {
      useAlert(t('SOCIAL_POST_MESSAGES.SUGGESTIONS_EMPTY'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.SUGGESTIONS_ERROR')
    );
  } finally {
    isGenerating.value = false;
  }
};

const insertSuggestion = (suggestion, e) => {
  e.stopPropagation();
  const text =
    typeof suggestion === 'object'
      ? suggestion.content || suggestion.text
      : suggestion;
  replyText.value = text;
  suggestions.value = [];
};

const generateReplySuggestions = async (reply, e) => {
  e.stopPropagation();
  if (generatingReplies.value[reply.id]) return;

  if (reply.content_attributes?.ai_suggestions?.length > 0) {
    replySuggestions.value = {
      ...replySuggestions.value,
      [reply.id]: reply.content_attributes.ai_suggestions,
    };
    return;
  }

  generatingReplies.value = { ...generatingReplies.value, [reply.id]: true };
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${reply.id}/suggest_replies`
    );
    const newSuggestions = response.data.suggestions || [];
    if (newSuggestions.length === 0) {
      useAlert(t('SOCIAL_POST_MESSAGES.SUGGESTIONS_EMPTY'));
    } else {
      replySuggestions.value = {
        ...replySuggestions.value,
        [reply.id]: newSuggestions,
      };
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.SUGGESTIONS_ERROR')
    );
  } finally {
    generatingReplies.value = { ...generatingReplies.value, [reply.id]: false };
  }
};

const insertReplySuggestion = (suggestion, replyId, e) => {
  e.stopPropagation();
  const text =
    typeof suggestion === 'object'
      ? suggestion.content || suggestion.text
      : suggestion;
  replyText.value = text;
  const newSugs = { ...replySuggestions.value };
  delete newSugs[replyId];
  replySuggestions.value = newSugs;
};

const isReplying = ref(false);
const showDeleteConfirm = ref(false);

const hideComment = async e => {
  e.stopPropagation();
  const targetHiddenState = !isHidden.value;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${messageId.value}/facebook_actions`,
      {
        fb_action: targetHiddenState ? 'hide' : 'unhide',
      }
    );
    localIsHidden.value = targetHiddenState;
    useAlert(
      targetHiddenState
        ? t('SOCIAL_POST_MESSAGES.COMMENT_HIDDEN_SUCCESS')
        : t('SOCIAL_POST_MESSAGES.COMMENT_VISIBLE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('SOCIAL_POST_MESSAGES.COMMENT_MODERATE_ERROR')
    );
  }
};

const toggleLike = async e => {
  e.stopPropagation();
  if (isChangingLike.value) return;

  const targetLikedState = !isLiked.value;
  isChangingLike.value = true;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${messageId.value}/facebook_actions`,
      {
        fb_action: targetLikedState ? 'like' : 'unlike',
      }
    );
    localIsLiked.value = targetLikedState;
    useAlert(
      targetLikedState
        ? t('SOCIAL_POST_MESSAGES.COMMENT_LIKE_SUCCESS')
        : t('SOCIAL_POST_MESSAGES.COMMENT_UNLIKE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.LIKE_UPDATE_ERROR')
    );
  } finally {
    isChangingLike.value = false;
  }
};

const localReplyLikes = ref({});
const replyChangingLike = ref({});

const isReplyLiked = reply => {
  if (localReplyLikes.value[reply.id] !== undefined) {
    return localReplyLikes.value[reply.id];
  }
  return (
    reply.content_attributes?.is_liked_by_business ||
    reply.content_attributes?.isLikedByBusiness ||
    false
  );
};

const toggleReplyLike = async (reply, e) => {
  e.stopPropagation();
  if (replyChangingLike.value[reply.id]) return;

  const targetLikedState = !isReplyLiked(reply);
  replyChangingLike.value = { ...replyChangingLike.value, [reply.id]: true };

  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${reply.id}/facebook_actions`,
      {
        fb_action: targetLikedState ? 'like' : 'unlike',
      }
    );
    localReplyLikes.value = {
      ...localReplyLikes.value,
      [reply.id]: targetLikedState,
    };
    useAlert(
      targetLikedState
        ? t('SOCIAL_POST_MESSAGES.COMMENT_LIKE_SUCCESS')
        : t('SOCIAL_POST_MESSAGES.COMMENT_UNLIKE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.LIKE_UPDATE_ERROR')
    );
  } finally {
    replyChangingLike.value = { ...replyChangingLike.value, [reply.id]: false };
  }
};

const hideReply = async (reply, e) => {
  e.stopPropagation();
  const currentlyHidden =
    localHiddenReplies.value[reply.id] !== undefined
      ? localHiddenReplies.value[reply.id]
      : reply.content_attributes?.is_hidden ||
        reply.content_attributes?.isHidden ||
        false;
  const targetHiddenState = !currentlyHidden;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${reply.id}/facebook_actions`,
      {
        fb_action: targetHiddenState ? 'hide' : 'unhide',
      }
    );
    localHiddenReplies.value = {
      ...localHiddenReplies.value,
      [reply.id]: targetHiddenState,
    };
    useAlert(
      targetHiddenState
        ? t('SOCIAL_POST_MESSAGES.REPLY_HIDE_SUCCESS')
        : t('SOCIAL_POST_MESSAGES.REPLY_SHOW_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.REPLY_HIDE_ERROR')
    );
  }
};

const deleteComment = () => {
  showDeleteConfirm.value = true;
};

const confirmDelete = async () => {
  showDeleteConfirm.value = false;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${messageId.value}/facebook_actions`,
      {
        fb_action: 'delete',
      }
    );

    localIsDeleted.value = true;

    useAlert(t('SOCIAL_POST_MESSAGES.COMMENT_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('SOCIAL_POST_MESSAGES.COMMENT_DELETE_ERROR')
    );
  }
};

const replyToDelete = ref(null);

const confirmDeleteReply = async reply => {
  replyToDelete.value = null;
  try {
    // If it's a failed message, it might not exist on Facebook, but we can still delete it locally via facebook_actions
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${reply.id}/facebook_actions`,
      {
        fb_action: 'delete',
      }
    );

    useAlert(t('SOCIAL_POST_MESSAGES.REPLY_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('SOCIAL_POST_MESSAGES.REPLY_DELETE_ERROR')
    );
  }
};

const publishPublicReply = async (text, successMessage) => {
  if (!text.trim() || isReplying.value) return false;

  isReplying.value = true;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages`,
      {
        content: text,
        content_attributes: {
          fb_reply_type: 'public',
          in_reply_to_external_id:
            igAttributes.value.fb_comment_id || igAttributes.value.fbCommentId,
          is_facebook_post_context: true,
          image_url: imageUrl.value,
          caption: caption.value,
          permalink: permalink.value,
        },
      }
    );
    useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_POST_MESSAGES.REPLY_SEND_ERROR')
    );
    return false;
  } finally {
    isReplying.value = false;
  }
};

const sendReply = async e => {
  e?.stopPropagation();
  const text = replyText.value.trim();
  const sent = await publishPublicReply(
    text,
    t('SOCIAL_POST_MESSAGES.REPLY_SEND_SUCCESS')
  );
  if (sent) replyText.value = '';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-col max-w-sm rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm transition-shadow hover:shadow-md"
    >
      <div
        class="flex items-center px-3 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800"
      >
        <Icon icon="brand-facebook" size="16" class="text-pink-500 mr-2" />
        <button
          type="button"
          class="text-xs font-medium text-slate-700 underline-offset-2 hover:underline dark:text-slate-300"
          :disabled="!permalink"
          @click="openPost"
        >
          {{ $t('FACEBOOK_POST.POST_CONTEXT') }}
        </button>
        <button
          v-if="hasLumerAIFeature"
          class="ml-auto flex items-center gap-1 text-xs px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded border border-purple-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-purple-50"
          :disabled="isGenerating || isDeleted || isHidden"
          :title="$t('FACEBOOK_POST.GENERATE_REPLY_SUGGESTIONS')"
          @click.stop="generateSuggestions"
        >
          <span v-if="isGenerating" class="animate-spin mr-1">
            {{ $t('FACEBOOK_POST.GENERATING') }}
          </span>
          <Icon icon="sparkles" size="12" />
          <span class="font-medium">{{ $t('FACEBOOK_POST.SUGGEST') }}</span>
        </button>
        <button
          v-else
          class="ml-auto flex items-center gap-1 text-xs px-2 py-1 bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 rounded border border-slate-200 dark:border-slate-700 cursor-not-allowed"
          :title="$t('FACEBOOK_POST.ADVANCED_PLAN_FEATURE')"
          disabled
        >
          <Icon icon="lock" size="12" />
          <span class="font-medium">{{ $t('FACEBOOK_POST.SUGGEST') }}</span>
        </button>
      </div>

      <template v-if="imageUrl && !imageLoadFailed">
        <div class="relative w-full h-48 bg-slate-100 dark:bg-slate-900">
          <img
            :src="imageUrl"
            class="w-full h-full object-cover"
            :alt="
              caption
                ? $t('FACEBOOK_POST.POST_IMAGE_ALT_WITH_CAPTION', { caption })
                : $t('FACEBOOK_POST.POST_IMAGE_ALT')
            "
            loading="lazy"
            @error="imageLoadFailed = true"
          />
        </div>

        <div v-if="caption" class="p-3">
          <p
            class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            {{ $t('FACEBOOK_POST.PUBLICATION') }}
          </p>
          <p class="text-sm text-slate-700 dark:text-slate-300 line-clamp-3">
            {{ caption }}
          </p>
        </div>
      </template>

      <div
        v-else-if="caption"
        class="flex h-32 items-center justify-center bg-slate-100 p-4 text-center text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400"
      >
        {{ $t('FACEBOOK_POST.IMAGE_UNAVAILABLE') }}
      </div>

      <div
        v-else-if="embedUrl"
        class="relative w-full h-[400px] bg-slate-100 dark:bg-slate-900 overflow-hidden"
      >
        <iframe
          :src="embedUrl"
          class="w-full h-full border-0"
          :title="$t('FACEBOOK_POST.ORIGINAL_POST_TITLE')"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
          scrolling="no"
        />
      </div>

      <!-- Message Text / Comment -->
      <div
        v-if="content"
        class="p-3 bg-indigo-50 dark:bg-indigo-900/20 border-t border-slate-100 dark:border-slate-800"
      >
        <div
          v-if="isDeleted"
          class="flex items-center gap-2 p-2 bg-red-100/50 dark:bg-red-900/30 rounded mb-2"
        >
          <Icon icon="i-lucide-trash-2" class="text-red-600" size="14" />
          <span class="text-sm font-medium text-red-600 italic">{{
            $t('FACEBOOK_POST.DELETED_COMMENT')
          }}</span>
        </div>
        <div
          v-else-if="isHidden"
          class="flex items-center gap-2 p-2 bg-slate-200 dark:bg-slate-800 rounded mb-2"
        >
          <Icon icon="i-lucide-eye-off" class="text-slate-500" />
          <span class="text-sm font-medium text-slate-500 italic">{{
            $t('FACEBOOK_POST.HIDDEN_COMMENT')
          }}</span>
        </div>
        <div class="flex items-start gap-2.5">
          <img
            v-if="commentAuthorAvatar"
            :src="commentAuthorAvatar"
            :alt="`Foto de ${commentAuthorName}`"
            class="mt-0.5 size-8 shrink-0 rounded-full object-cover"
            loading="lazy"
          />
          <div
            v-else
            class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-pink-100 text-[11px] font-bold text-pink-700 dark:bg-pink-900/30 dark:text-pink-300"
            aria-hidden="true"
          >
            {{ commentAuthorInitials || 'IG' }}
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="mb-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400"
            >
              {{ $t('FACEBOOK_POST.COMMENT_BY', { name: commentAuthorName }) }}
            </p>
            <p
              class="text-sm font-medium whitespace-pre-wrap transition-all"
              :class="
                isDeleted || isHidden
                  ? 'line-through text-slate-400 dark:text-slate-500 italic'
                  : 'text-slate-800 dark:text-slate-200'
              "
            >
              {{ content }}
            </p>
          </div>
        </div>

        <div
          v-if="orientation !== 'right' && !isDeleted"
          class="mt-3 flex items-center justify-between border-t border-indigo-100 dark:border-indigo-800/30 pt-2"
        >
          <!-- Normal action buttons -->
          <template v-if="!showDeleteConfirm">
            <button
              class="flex flex-1 justify-center items-center gap-1.5 py-1.5 text-xs font-medium transition-colors rounded-md hover:bg-pink-50 hover:text-pink-700 dark:hover:bg-pink-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
              :class="
                isLiked ? 'text-pink-600 dark:text-pink-400' : 'text-slate-500'
              "
              :disabled="isChangingLike || isHidden"
              :title="
                isLiked
                  ? 'Remover curtida do comentário'
                  : 'Curtir comentário pela API oficial da Meta'
              "
              :aria-label="
                isLiked
                  ? 'Descurtir este comentário no Facebook'
                  : 'Curtir este comentário no Facebook'
              "
              :aria-pressed="isLiked"
              @click="toggleLike"
            >
              <Icon
                v-if="!isLiked"
                :icon="isChangingLike ? 'i-lucide-loader-2' : 'i-lucide-heart'"
                class="text-sm"
                :class="{ 'animate-spin': isChangingLike }"
              />
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-4 h-4"
              >
                <path
                  d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"
                />
              </svg>
              <span>
                {{
                  isLiked ? $t('FACEBOOK_POST.LIKED') : $t('FACEBOOK_POST.LIKE')
                }}
              </span>
            </button>

            <button
              class="flex flex-1 justify-center items-center gap-1.5 py-1.5 text-xs font-medium transition-colors rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/50"
              :class="
                isHidden
                  ? 'text-blue-500'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
              "
              :title="
                isHidden
                  ? $t('FACEBOOK_POST.SHOW_COMMENT')
                  : $t('FACEBOOK_POST.HIDE_COMMENT')
              "
              @click="hideComment"
            >
              <Icon
                :icon="isHidden ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                class="text-sm"
              />
              <span>
                {{
                  isHidden ? $t('FACEBOOK_POST.SHOW') : $t('FACEBOOK_POST.HIDE')
                }}
              </span>
            </button>

            <button
              class="flex flex-1 justify-center items-center gap-1.5 py-1.5 text-xs font-medium text-slate-500 transition-colors rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
              :class="
                !isHidden
                  ? 'hover:text-red-600 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                  : ''
              "
              :disabled="isHidden"
              :title="$t('FACEBOOK_POST.DELETE_COMMENT')"
              @click="deleteComment"
            >
              <Icon icon="i-lucide-trash-2" class="text-sm" />
              <span>{{ $t('FACEBOOK_POST.DELETE') }}</span>
            </button>
          </template>

          <!-- Inline deletion confirm -->
          <div
            v-else
            class="flex flex-1 items-center justify-between px-2 bg-red-50 dark:bg-red-900/20 rounded-md py-1 animate-fade-in"
          >
            <span
              class="text-xs font-semibold text-red-600 dark:text-red-400"
              >{{ $t('FACEBOOK_POST.DELETE_FOREVER') }}</span
            >
            <div class="flex items-center gap-2">
              <button
                class="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                @click="showDeleteConfirm = false"
              >
                {{ $t('DIALOG.BUTTONS.CANCEL') }}
              </button>
              <button
                class="px-2 py-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded transition-colors"
                @click="confirmDelete"
              >
                {{ $t('FACEBOOK_POST.DELETE') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Replies Thread -->
      <div
        v-if="replies.length > 0 && orientation !== 'right'"
        class="bg-slate-50 dark:bg-slate-900/50 flex flex-col border-t border-slate-200 dark:border-slate-700"
      >
        <div
          v-for="reply in replies"
          :key="reply.id"
          class="p-3 border-b border-slate-100 dark:border-slate-800 last:border-0 flex gap-2"
          :class="replyThreadClass(reply.threadDepth)"
        >
          <img
            v-if="reply.sender?.thumbnail"
            :src="reply.sender.thumbnail"
            class="w-7 h-7 rounded-full object-cover shrink-0"
          />
          <div
            v-else
            class="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0"
          >
            <span class="text-[11px] font-bold text-purple-600">{{
              reply.sender?.name?.charAt(0) || 'V'
            }}</span>
          </div>
          <div
            v-if="replyToDelete === reply.id"
            class="flex-1 flex items-center justify-between px-2 bg-red-50 dark:bg-red-900/20 rounded-md py-2 animate-fade-in"
          >
            <span
              class="text-xs font-semibold text-red-600 dark:text-red-400"
              >{{ $t('FACEBOOK_POST.DELETE_REPLY') }}</span
            >
            <div class="flex items-center gap-2">
              <button
                class="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                @click="replyToDelete = null"
              >
                {{ $t('DIALOG.BUTTONS.CANCEL') }}
              </button>
              <button
                class="px-2 py-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded transition-colors"
                @click="confirmDeleteReply(reply)"
              >
                {{ $t('FACEBOOK_POST.DELETE') }}
              </button>
            </div>
          </div>

          <div v-else class="flex-1 flex flex-col group relative">
            <div class="flex items-center justify-between mb-0.5">
              <span
                class="text-xs font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1"
              >
                {{ reply.sender?.name || 'Você' }}
                <Icon
                  v-if="reply.sender_type !== 'Contact'"
                  icon="i-lucide-badge-check"
                  class="text-blue-500 text-[12px]"
                />
              </span>
              <div class="flex items-center gap-2">
                <span
                  v-if="reply.status === 'failed'"
                  class="flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded"
                >
                  <Icon icon="i-lucide-alert-circle" size="10" />
                  {{ $t('FACEBOOK_POST.SEND_FAILED') }}
                </span>

                <button
                  v-if="
                    hasLumerAIFeature &&
                    !reply.content_attributes?.deleted &&
                    !isDeleted &&
                    !(
                      localHiddenReplies[reply.id] ??
                      (reply.content_attributes?.is_hidden ||
                        reply.content_attributes?.isHidden)
                    ) &&
                    (!replySuggestions[reply.id] ||
                      replySuggestions[reply.id].length === 0)
                  "
                  class="opacity-0 group-hover:opacity-100 text-purple-500 hover:text-purple-700 transition-opacity flex items-center gap-1 text-[10px] font-medium mr-1"
                  :title="$t('FACEBOOK_POST.SUGGEST_REPLY')"
                  @click="generateReplySuggestions(reply, $event)"
                >
                  <Icon
                    :icon="
                      generatingReplies[reply.id]
                        ? 'i-lucide-loader-2'
                        : 'sparkles'
                    "
                    :class="{ 'animate-spin': generatingReplies[reply.id] }"
                    size="12"
                  />
                  {{ $t('FACEBOOK_POST.SUGGEST') }}
                </button>

                <button
                  v-if="!reply.content_attributes?.deleted && !isDeleted"
                  class="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[10px] font-medium mr-1"
                  :class="
                    isReplyLiked(reply)
                      ? 'text-pink-600 hover:text-pink-700 dark:text-pink-400'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
                  "
                  :title="
                    isReplyLiked(reply)
                      ? 'Remover curtida da resposta'
                      : 'Curtir resposta'
                  "
                  @click="toggleReplyLike(reply, $event)"
                >
                  <Icon
                    v-if="!isReplyLiked(reply)"
                    :icon="
                      replyChangingLike[reply.id]
                        ? 'i-lucide-loader-2'
                        : 'i-lucide-heart'
                    "
                    :class="{ 'animate-spin': replyChangingLike[reply.id] }"
                    size="12"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="12"
                    height="12"
                  >
                    <path
                      d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"
                    />
                  </svg>
                </button>

                <button
                  v-if="!reply.content_attributes?.deleted && !isDeleted"
                  class="opacity-0 group-hover:opacity-100 transition-opacity"
                  :class="
                    (localHiddenReplies[reply.id] ??
                    (reply.content_attributes?.is_hidden ||
                      reply.content_attributes?.isHidden))
                      ? 'text-blue-500 hover:text-blue-700'
                      : 'text-slate-400 hover:text-slate-600'
                  "
                  :title="
                    (localHiddenReplies[reply.id] ??
                    (reply.content_attributes?.is_hidden ||
                      reply.content_attributes?.isHidden))
                      ? 'Exibir resposta'
                      : 'Ocultar resposta'
                  "
                  @click="hideReply(reply, $event)"
                >
                  <Icon
                    :icon="
                      (localHiddenReplies[reply.id] ??
                      (reply.content_attributes?.is_hidden ||
                        reply.content_attributes?.isHidden))
                        ? 'i-lucide-eye'
                        : 'i-lucide-eye-off'
                    "
                    class="text-sm"
                  />
                </button>

                <button
                  v-if="!reply.content_attributes?.deleted && !isDeleted"
                  class="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
                  :title="$t('FACEBOOK_POST.DELETE_REPLY_TITLE')"
                  @click="replyToDelete = reply.id"
                >
                  <Icon icon="i-lucide-trash-2" class="text-sm" />
                </button>
              </div>
            </div>

            <div
              v-if="
                localHiddenReplies[reply.id] ??
                (reply.content_attributes?.is_hidden ||
                  reply.content_attributes?.isHidden)
              "
              class="flex items-center gap-1.5 p-1.5 bg-slate-100 dark:bg-slate-800 rounded mb-1 inline-flex self-start"
            >
              <Icon icon="i-lucide-eye-off" class="text-slate-500" size="12" />
              <span class="text-[10px] font-medium text-slate-500 italic">{{
                $t('FACEBOOK_POST.HIDDEN_REPLY')
              }}</span>
            </div>

            <span
              class="text-sm whitespace-pre-wrap transition-all"
              :class="
                reply.content_attributes?.deleted ||
                isDeleted ||
                (localHiddenReplies[reply.id] ??
                  (reply.content_attributes?.is_hidden ||
                    reply.content_attributes?.isHidden))
                  ? 'line-through text-slate-400 dark:text-slate-500 italic'
                  : 'text-slate-800 dark:text-slate-200'
              "
              >{{ reply.content }}</span
            >
            <span
              v-if="reply.content_attributes?.deleted"
              class="text-[10px] font-medium text-red-500 italic mt-0.5"
              >{{ $t('FACEBOOK_POST.DELETED_REPLY') }}</span
            >
            <span
              v-else-if="isDeleted"
              class="text-[10px] font-medium text-red-400/80 italic mt-0.5"
              >{{ $t('FACEBOOK_POST.REPLY_DELETED_WITH_COMMENT') }}</span
            >

            <!-- Suggestions for this specific reply -->
            <div
              v-if="
                replySuggestions[reply.id] &&
                replySuggestions[reply.id].length > 0 &&
                !reply.content_attributes?.deleted &&
                !isDeleted &&
                !(
                  localHiddenReplies[reply.id] ??
                  (reply.content_attributes?.is_hidden ||
                    reply.content_attributes?.isHidden)
                )
              "
              class="mt-2 flex flex-col gap-1.5 p-2 bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/30 rounded-lg"
            >
              <div
                class="text-[10px] font-semibold text-purple-600 dark:text-purple-400 flex items-center justify-between"
              >
                <span class="flex items-center"
                  ><Icon icon="sparkles" size="10" class="mr-1" />
                  {{ $t('FACEBOOK_POST.REPLY_SUGGESTIONS') }}</span
                >
                <button
                  class="text-purple-400 hover:text-purple-600"
                  @click="replySuggestions[reply.id] = []"
                >
                  <Icon icon="i-lucide-x" size="10" />
                </button>
              </div>
              <button
                v-for="(sug, index) in replySuggestions[reply.id]"
                :key="index"
                class="text-left text-xs px-2 py-1.5 bg-white dark:bg-slate-800 border border-purple-100 dark:border-purple-800/50 rounded shadow-sm hover:bg-purple-50 dark:hover:bg-purple-900/40 text-slate-700 dark:text-slate-300 transition-colors"
                @click="insertReplySuggestion(sug, reply.id, $event)"
              >
                {{ typeof sug === 'object' ? sug.content || sug.text : sug }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Suggestions Box -->
      <div
        v-if="
          hasLumerAIFeature &&
          suggestions.length > 0 &&
          orientation !== 'right' &&
          !isHidden &&
          !isDeleted
        "
        class="flex flex-col gap-1.5 p-3 bg-purple-50/50 dark:bg-purple-900/10 border-t border-purple-100 dark:border-purple-800/30"
      >
        <div
          class="text-[11px] font-semibold text-purple-600 dark:text-purple-400 flex items-center mb-1"
        >
          <Icon icon="sparkles" size="12" class="mr-1" />
          {{ $t('FACEBOOK_POST.SUGGESTIONS_HINT') }}
        </div>
        <button
          v-for="(sug, index) in suggestions"
          :key="index"
          class="text-left text-xs px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-800/50 rounded-lg shadow-sm hover:bg-purple-50 dark:hover:bg-purple-900/40 text-slate-700 dark:text-slate-300 transition-colors"
          @click="e => insertSuggestion(sug, e)"
        >
          {{ typeof sug === 'object' ? sug.content || sug.text : sug }}
        </button>
      </div>

      <!-- Public Reply Box -->
      <div
        v-if="orientation !== 'right' && !isHidden && !isDeleted"
        class="flex items-center p-3 border-t border-slate-100 dark:border-slate-800/60"
        :class="{
          'border-t-0': replies.length > 0 && suggestions.length === 0,
        }"
      >
        <input
          v-model="replyText"
          type="text"
          class="flex-1 text-sm !bg-transparent !border-none !shadow-none !p-0 !m-0 !focus:outline-none !focus:ring-0 text-slate-800 dark:text-slate-200 placeholder-slate-400"
          :placeholder="$t('FACEBOOK_POST.COMMENT_PLACEHOLDER')"
          @keyup.enter="sendReply"
          @click.stop
        />
        <button
          class="ml-2 text-sm font-semibold text-blue-500 hover:text-blue-600 disabled:opacity-40 bg-transparent transition-colors"
          :disabled="!replyText.trim() || isReplying"
          :title="$t('FACEBOOK_POST.SEND_PUBLIC_REPLY')"
          @click="sendReply"
        >
          <span
            v-if="isReplying"
            class="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"
          />
          <span v-else>{{ $t('FACEBOOK_POST.PUBLISH') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
